import React, { useState, useEffect, useMemo } from 'react';
import { FaTimes, FaSave, FaSpinner, FaUpload, FaTrash } from 'react-icons/fa';
import { TrainingType, TrainingInstitute, TrainingRequest } from '../types';
import { CloseModal } from '../../sites/closemodal';
import type { SelectOption } from '../../../utils/select-with-search';
import { SearchableSelect } from '../../../utils/select-with-search';
import api from '../../../libs/axios';

interface AddPaymentOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  trainingTypes: TrainingType[];
  institutes: TrainingInstitute[];
  trainingRequests?: TrainingRequest[];
  loading?: boolean;
}

export const AddPaymentOffcanvas: React.FC<AddPaymentOffcanvasProps> = ({
  isOpen,
  onClose,
  onSubmit,
  trainingTypes,
  institutes,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    training_institute_id: '',
    training_type_id: '',
    pi_number: '',
    pi_date: '',
    invoice_number: '',
    invoice_date: '',
    pi_basic_amount: '',
    pi_tax_amount: '',
    pi_total_amount: '',
    invoice_basic_amount: '',
    invoice_tax_amount: '',
    invoice_total_amount: '',
    per_person_amount: '',
    no_of_candidates: '',
    payment_type: 'advance',
    candidate_ids: [] as string[],
    pi_candidate_ids: [] as string[],
  });
  const [selectedInstitute, setSelectedInstitute] = useState<SelectOption | null>(null);
  const [selectedType, setSelectedType] = useState<SelectOption | null>(null);
  const [availablePiRequests, setAvailablePiRequests] = useState<SelectOption[]>([]);
  const [availableInvoiceRequests, setAvailableInvoiceRequests] = useState<SelectOption[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState<SelectOption[]>([]);
  const [selectedPiRequests, setSelectedPiRequests] = useState<SelectOption[]>([]);
  const [invoiceDocuments, setInvoiceDocuments] = useState<File[]>([]);
  const [pibDocuments, setPibDocuments] = useState<File[]>([]);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        training_institute_id: '',
        training_type_id: '',
        pi_number: '',
        pi_date: '',
        invoice_number: '',
        invoice_date: '',
        pi_basic_amount: '',
        pi_tax_amount: '',
        pi_total_amount: '',
        invoice_basic_amount: '',
        invoice_tax_amount: '',
        invoice_total_amount: '',
        per_person_amount: '',
        no_of_candidates: '',
        payment_type: 'advance',
        candidate_ids: [],
        pi_candidate_ids: [],
      });
      setSelectedInstitute(null);
      setSelectedType(null);
      setAvailablePiRequests([]);
      setAvailableInvoiceRequests([]);
      setRequestsLoading(false);
      setErrors({});
      setShowCloseModal(false);
      setSelectedRequests([]);
      setSelectedPiRequests([]);
      setInvoiceDocuments([]);
      setPibDocuments([]);
    }
  }, [isOpen]);

  const requestClose = () => {
    if (loading) return;
    setShowCloseModal(true);
  };

  const confirmClose = () => {
    setShowCloseModal(false);
    onClose();
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.training_institute_id) newErrors.training_institute_id = 'Required';
    if (!formData.training_type_id) newErrors.training_type_id = 'Required';
    if (!formData.payment_type) newErrors.payment_type = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const instituteOptions = useMemo<SelectOption[]>(() =>
    institutes.map(i => ({ id: i.id, value: i.id, label: i.institute_name })),
    [institutes]
  );

  const typeOptions = useMemo<SelectOption[]>(() =>
    trainingTypes.map(t => ({ id: t.id, value: t.id, label: t.training_name })),
    [trainingTypes]
  );

  const handleInstituteChange = (option: SelectOption | null) => {
    setSelectedInstitute(option);
    setFormData(prev => ({ ...prev, training_institute_id: option ? String(option.value) : '' }));
    if (errors.training_institute_id) setErrors(prev => ({ ...prev, training_institute_id: '' }));
  };

  const handleTypeChange = (option: SelectOption | null) => {
    setSelectedType(option);
    setFormData(prev => ({ ...prev, training_type_id: option ? String(option.value) : '' }));
    if (errors.training_type_id) setErrors(prev => ({ ...prev, training_type_id: '' }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData: any = {
        training_institute_id: Number(formData.training_institute_id),
        training_type_id: Number(formData.training_type_id),
        payment_type: formData.payment_type,
      };
      if (formData.pi_number) submitData.pi_number = formData.pi_number;
      if (formData.pi_date) submitData.pi_date = formData.pi_date;
      if (formData.invoice_number) submitData.invoice_number = formData.invoice_number;
      if (formData.invoice_date) submitData.invoice_date = formData.invoice_date;
      if (formData.pi_basic_amount) submitData.pi_basic_amount = Number(formData.pi_basic_amount);
      if (formData.pi_tax_amount) submitData.pi_tax_amount = Number(formData.pi_tax_amount);
      if (formData.pi_total_amount) submitData.pi_total_amount = Number(formData.pi_total_amount);
      if (formData.invoice_basic_amount) submitData.invoice_basic_amount = Number(formData.invoice_basic_amount);
      if (formData.invoice_tax_amount) submitData.invoice_tax_amount = Number(formData.invoice_tax_amount);
      if (formData.invoice_total_amount) submitData.invoice_total_amount = Number(formData.invoice_total_amount);
      if (formData.per_person_amount) submitData.per_person_amount = Number(formData.per_person_amount);
      if (formData.no_of_candidates) submitData.no_of_candidates = Number(formData.no_of_candidates);
      if (formData.candidate_ids.length) {
        submitData.candidate_ids = formData.candidate_ids
          .map((id) => Number(id))
          .filter((id) => !isNaN(id));
      }
      if (formData.pi_candidate_ids.length) {
        submitData.pi_candidate_ids = formData.pi_candidate_ids
          .map((id) => Number(id))
          .filter((id) => !isNaN(id));
      }
      submitData._payment_documents = [
        ...invoiceDocuments.map((file) => ({ file, document_type: "invoice" })),
        ...pibDocuments.map((file) => ({ file, document_type: "pib" })),
      ];
      onSubmit(submitData);
    }
  };

  const handleDocumentSelect = (
    files: FileList | null,
    setter: React.Dispatch<React.SetStateAction<File[]>>
  ) => {
    if (!files) return;
    setter((prev) => [...prev, ...Array.from(files)]);
  };

  const renderDocumentPicker = (
    label: string,
    files: File[],
    setter: React.Dispatch<React.SetStateAction<File[]>>
  ) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <label className="flex items-center gap-2 w-full px-3 py-2 border border-dashed rounded cursor-pointer text-sm border-gray-300 hover:border-blue-400 hover:bg-blue-50">
        <FaUpload className="text-gray-400" />
        <span className="text-gray-500">{files.length > 0 ? `${files.length} file(s) selected` : "Upload documents"}</span>
        <input
          type="file"
          multiple
          className="hidden"
          disabled={loading}
          accept="*/*"
          onChange={(e) => {
            handleDocumentSelect(e.target.files, setter);
            e.target.value = "";
          }}
        />
      </label>
      {files.length > 0 && (
        <div className="mt-2 space-y-2">
          {files.map((file, index) => (
            <div key={`${file.name}-${index}`} className="flex items-center justify-between rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
              <span className="truncate text-gray-700">{file.name}</span>
              <button type="button" onClick={() => setter((prev) => prev.filter((_, i) => i !== index))} className="text-red-600 hover:text-red-800">
                <FaTrash className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleRequestSelectChange = (options: SelectOption[] = []) => {
    setSelectedRequests(options);
    setFormData((prev) => ({
      ...prev,
      candidate_ids: options.map((option) => String(option.value)),
    }));
  };

  const handlePiRequestSelectChange = (options: SelectOption[] = []) => {
    setSelectedPiRequests(options);
    setFormData((prev) => ({
      ...prev,
      pi_candidate_ids: options.map((option) => String(option.value)),
    }));
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen && !loading) requestClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading]);

  useEffect(() => {
    setSelectedRequests([]);
    setSelectedPiRequests([]);
    setFormData(prev => ({ ...prev, candidate_ids: [], pi_candidate_ids: [] }));

    if (!formData.training_type_id || !formData.training_institute_id) {
      setAvailablePiRequests([]);
      setAvailableInvoiceRequests([]);
      return;
    }

    let cancelled = false;
    const fetchRequests = async () => {
      setRequestsLoading(true);
      try {
        const res = await api.get("/training/payments/candidates/available", {
          params: {
            training_institute_id: Number(formData.training_institute_id),
            training_type_id: Number(formData.training_type_id),
          },
        });
        if (!cancelled && res.data.success) {
          const toOption = (r: any): SelectOption => ({
            id: r.id, value: r.id,
            label: r.request_id ? `${r.request_id}${r.candidate_name ? ` - ${r.candidate_name}` : ""}` : `Request #${r.id}`,
          });
          setAvailablePiRequests((res.data.data?.pi_candidates ?? []).map(toOption));
          setAvailableInvoiceRequests((res.data.data?.invoice_candidates ?? []).map(toOption));
        }
      } catch {
        if (!cancelled) { setAvailablePiRequests([]); setAvailableInvoiceRequests([]); }
      } finally {
        if (!cancelled) setRequestsLoading(false);
      }
    };

    fetchRequests();
    return () => { cancelled = true; };
  }, [formData.training_type_id, formData.training_institute_id]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={!loading ? requestClose : undefined} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">New Training Payment</h2>
            <p className="text-sm text-gray-600 mt-1">Create a payment record for training.</p>
          </div>
          <button onClick={requestClose} className="p-2 hover:bg-gray-200 rounded" disabled={loading}><FaTimes className="w-5 h-5 text-gray-600" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Institute *</label>
                <SearchableSelect
                  value={selectedInstitute}
                  onChange={handleInstituteChange}
                  options={instituteOptions}
                  placeholder="Select institute"
                  disabled={loading}
                  error={errors.training_institute_id}
                  className="w-full"
                  minSearchLength={0}
                />
                {errors.training_institute_id && <p className="mt-1 text-sm text-red-600">{errors.training_institute_id}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Training Type *</label>
                <SearchableSelect
                  value={selectedType}
                  onChange={handleTypeChange}
                  options={typeOptions}
                  placeholder="Select type"
                  disabled={loading}
                  error={errors.training_type_id}
                  className="w-full"
                  minSearchLength={0}
                />
                {errors.training_type_id && <p className="mt-1 text-sm text-red-600">{errors.training_type_id}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Type *</label>
              <select name="payment_type" value={formData.payment_type} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm bg-white">
                <option value="advance">Advance</option>
                <option value="post_payment">Post Payment</option>
              </select>
            </div>

            {/* PI Fields */}
            <div className="border border-gray-200 rounded p-4 space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">PI Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PI Number</label>
                  <input type="text" name="pi_number" value={formData.pi_number} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="PI-2026-001" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PI Date</label>
                  <input type="date" name="pi_date" value={formData.pi_date} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PI Basic Amount</label>
                  <input type="number" name="pi_basic_amount" value={formData.pi_basic_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="50000" step="0.01" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PI Tax Amount</label>
                  <input type="number" name="pi_tax_amount" value={formData.pi_tax_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="9000" step="0.01" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PI Total Amount</label>
                  <input type="number" name="pi_total_amount" value={formData.pi_total_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="59000" step="0.01" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PI Training IDs</label>
                <SearchableSelect
                  value={selectedPiRequests}
                  onChange={handlePiRequestSelectChange}
                  options={availablePiRequests}
                  isLoading={requestsLoading}
                  placeholder={(!formData.training_type_id || !formData.training_institute_id) ? "Select institute and training type first" : (requestsLoading ? "Loading requests..." : "Search by request ID or candidate name")}
                  disabled={loading || !formData.training_type_id || !formData.training_institute_id}
                  isMulti={true}
                  closeOnOutsideClick={false}
                  minSearchLength={0}
                  className="w-full"
                />
                <p className="mt-1 text-xs text-gray-500">Select one or more training requests for PI</p>
              </div>
            </div>

            {/* Invoice Fields */}
            <div className="border border-gray-200 rounded p-4 space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Number</label>
                  <input type="text" name="invoice_number" value={formData.invoice_number} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="INV-2026-001" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input type="date" name="invoice_date" value={formData.invoice_date} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Basic Amount</label>
                  <input type="number" name="invoice_basic_amount" value={formData.invoice_basic_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="50000" step="0.01" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Tax Amount</label>
                  <input type="number" name="invoice_tax_amount" value={formData.invoice_tax_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="9000" step="0.01" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Total Amount</label>
                  <input type="number" name="invoice_total_amount" value={formData.invoice_total_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="59000" step="0.01" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Training IDs</label>
                <SearchableSelect
                  value={selectedRequests}
                  onChange={handleRequestSelectChange}
                  options={availableInvoiceRequests}
                  isLoading={requestsLoading}
                  placeholder={(!formData.training_type_id || !formData.training_institute_id) ? "Select institute and training type first" : (requestsLoading ? "Loading requests..." : "Search by request ID or candidate name")}
                  disabled={loading || !formData.training_type_id || !formData.training_institute_id}
                  isMulti={true}
                  closeOnOutsideClick={false}
                  minSearchLength={0}
                  className="w-full"
                />
                <p className="mt-1 text-xs text-gray-500">Select one or more training requests for invoice</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Per Person Amount</label>
                <input type="number" name="per_person_amount" value={formData.per_person_amount} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="5900" step="0.01" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">No. of Candidates</label>
                <input type="number" name="no_of_candidates" value={formData.no_of_candidates} onChange={handleInputChange} disabled={loading} className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm" placeholder="10" min="1" />
              </div>
            </div>

            <div className="border border-gray-200 rounded p-4 space-y-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Documents</p>
              {renderDocumentPicker("Invoice Documents", invoiceDocuments, setInvoiceDocuments)}
              {renderDocumentPicker("PIB Documents", pibDocuments, setPibDocuments)}
            </div>
          </form>
        </div>

        <div className="border-t bg-gray-50 px-6 py-4 flex-shrink-0">
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={requestClose} disabled={loading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">Cancel</button>
            <button type="button" onClick={handleSubmit} disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-blue-800 rounded hover:bg-blue-950 disabled:opacity-50 flex items-center gap-2">
              {loading ? (<><FaSpinner className="w-4 h-4 animate-spin" />Creating...</>) : (<><FaSave className="w-4 h-4" />Create Payment</>)}
            </button>
          </div>
        </div>
      </div>
      <CloseModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={confirmClose}
      />
    </>
  );
};
